import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * One-time username claim. Immutable after set (backend enforces 409). The
 * modal is deliberately un-dismissable — it appears once, requires a valid
 * handle, and never bothers the user again after they've picked. Pre-fills
 * a suggested slug derived from the caller's Google display name.
 */
export default function UsernameClaimModal({ onClaimed }) {
  const [handle, setHandle] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [busy, setBusy] = useState(false);
  const checkTimer = useRef(null);
  const seedTried = useRef(false);

  // Ask the backend for a suggested handle exactly once when the modal opens.
  useEffect(() => {
    if (seedTried.current) return;
    seedTried.current = true;
    (async () => {
      try {
        const r = await api.get("/commons/profile/username-suggest");
        if (r.data.suggested) {
          setHandle(r.data.suggested);
          // Kick off a first availability check so the green tick appears.
          runCheck(r.data.suggested);
        }
      } catch { /* fall through — user types their own */ }
    })();
  }, []);

  // Hide the global Add FAB while the modal is open.
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);

  function onChange(e) {
    const raw = e.target.value.toLowerCase().replace(/\s+/g, "_").slice(0, 20);
    setHandle(raw);
    clearTimeout(checkTimer.current);
    setStatus({ state: "checking", msg: "…" });
    checkTimer.current = setTimeout(() => runCheck(raw), 260);
  }

  async function runCheck(v) {
    if (!v) { setStatus({ state: "idle", msg: "" }); return; }
    try {
      const r = await api.get("/commons/profile/username-available", { params: { u: v } });
      if (r.data.available) {
        setStatus({ state: "ok", msg: "available" });
      } else if (r.data.reason === "invalid") {
        setStatus({ state: "invalid", msg: "3–20 chars: letters, numbers, _ or -" });
      } else {
        setStatus({ state: "taken", msg: "already taken" });
      }
    } catch {
      setStatus({ state: "idle", msg: "" });
    }
  }

  async function submit() {
    if (status.state !== "ok" || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/commons/profile/username", { username: handle });
      toast.success(`welcome, @${r.data.username}`);
      onClaimed(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not save handle";
      toast.error(detail);
    } finally { setBusy(false); }
  }

  const ok = status.state === "ok";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      role="dialog" aria-modal="true" aria-labelledby="claim-title"
      data-testid="username-claim-modal"
    >
      <div className="ornate-frame max-w-md w-full">
        <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80 text-center">
          the commons
        </p>
        <h2 id="claim-title" className="mt-2 font-serif-lit text-2xl sm:text-3xl text-[var(--cream)] text-center leading-tight">
          pick a name others can find you by
        </h2>
        <p className="mt-2 font-garamond italic text-[var(--cream-dim)] text-center text-sm">
          this is your handle forever — like a signature. 3–20 characters, letters, numbers, underscore or dash.
        </p>

        <div className="mt-6">
          <label className="flex items-center gap-2 border-b border-[rgba(179,137,90,0.28)] pb-1.5 focus-within:border-[var(--brass)] transition-colors">
            <span className="font-mono-lc text-sm text-[var(--brass)]">@</span>
            <input
              type="text"
              value={handle}
              onChange={onChange}
              placeholder="pick a handle"
              autoFocus
              maxLength={20}
              data-testid="username-input"
              className="flex-1 bg-transparent outline-none font-mono-lc text-lg text-[var(--cream)] placeholder-[var(--cream-mute)]"
            />
            {ok && <Check size={16} strokeWidth={2} className="text-[#7A9E7A] shrink-0" />}
            {(status.state === "taken" || status.state === "invalid") && (
              <AlertCircle size={16} strokeWidth={1.8} className="text-[#C86A5C] shrink-0" />
            )}
          </label>
          <p
            data-testid="username-status"
            className={`mt-2 font-mono-lc text-[10px] tracking-[0.16em] uppercase h-3 ${
              ok ? "text-[#7A9E7A]" : (status.state === "taken" || status.state === "invalid") ? "text-[#C86A5C]" : "text-[var(--cream-mute)]"
            }`}
          >
            {status.msg}
          </p>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={submit}
            disabled={!ok || busy}
            data-testid="username-claim-btn"
            className="lc-btn"
          >
            {busy ? "saving…" : "claim handle"}
          </button>
        </div>
      </div>
    </div>
  );
}
